import { useMemo } from 'react';
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators
} from '@mui/x-data-grid-premium';
import { DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import moment from 'moment';
import { toBill } from 'services/financial';

import { calculateSponsorStatus } from 'components/pages/finance/NewInvoice/InvoiceTable/InvoiceTableService';
import { PARENT, WITHHOLDING } from 'components/pages/finance/NewInvoice/InvoiceTable/TableConstants';
import { Amount } from 'components/pages/finance/shared/amount-view/Amount/Amount';
import { overrideBeforeDateOperator } from 'components/pages/finance/shared/FinanseTableMUI/CustomFilterOperators';
import { DefaultFinanceCell } from 'components/pages/finance/shared/FinanseTableMUI/DefaultFinanceCell';
import { prepareNameForCSV } from 'components/pages/finance/shared/FinanseTableMUI/FinanceTableServices';
import { isWithholdingType } from 'components/pages/finance/SiteFinance/SiteFinanceInfo/SiteFinanceInfoServices';
import {
  convertPatientStatus,
  DefaultDateRenderCell
} from 'components/pages/finance/SiteFinance/SiteFinanceTable/SiteFinanceTable';
import { SubjectIdCell } from 'components/pages/finance/SiteFinance/SiteFinanceTable/SubjectIdCell/SubjectIdCell';
import { eventNumberComparator, resolveEventSuffix } from 'components/pages/finance/SitePayments/SitePaymentServices';
import {
  SITE_CREDIT,
  SITE_CREDIT_WH,
  SITE_DEBIT,
  SITE_DEBIT_WH,
  SitePaymentStatus
} from 'components/pages/finance/SitePayments/SitePaymentsTable/SitePaymentsConstants';
import {
  sitePaymentsDateRenderCell,
  sitePaymentsDateValueFormatter,
  sitePaymentsValueGetter
} from 'components/pages/finance/SitePayments/SitePaymentsTable/SitePaymentsTable';
import {
  PaymentStatusIcon,
  SitePaymentStatusIcon,
  StatusCell
} from 'components/pages/finance/SitePayments/SitePaymentsTable/StatusCell/StatusCell';

const ARColumns = ['invoiceDate', 'invoiceNumber', 'invoiceAmount', 'paymentStatus'];
const allStringOperators = getGridStringOperators();
const allNumberOperators = getGridNumericOperators();
const allSingleSelectOperators = getGridSingleSelectOperators();
const allDateOperators = getGridDateOperators();

export const useSiteFinanceTableColumns = userAbleToSeeARInfo => {
  return useMemo(() => {
    const defaultColumns = [
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1,
        minWidth: 110,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        filterOperators: allStringOperators
      },
      {
        field: 'pcn',
        headerName: 'PCN',
        flex: 1,
        minWidth: 100,
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1,
        minWidth: 100,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        filterOperators: allStringOperators
      },
      {
        field: 'subjectId',
        headerName: 'Subject ID',
        flex: 1,
        minWidth: 145,
        valueFormatter: value => (value != null ? `[${value}]` : ''),
        renderCell: SubjectIdCell,
        filterOperators: allStringOperators
      },
      {
        field: 'epochName',
        headerName: 'Epoch',
        flex: 1,
        minWidth: 110,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        filterOperators: allStringOperators
      },
      {
        field: 'encounterName',
        headerName: 'Encounter',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        filterOperators: allStringOperators
      },
      {
        field: 'itemGroupName',
        headerName: 'Procedure',
        flex: 1,
        minWidth: 140,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        filterOperators: allStringOperators
      },
      {
        field: 'eventName',
        headerName: 'Event Name',
        flex: 1,
        minWidth: 150,
        valueFormatter: value => prepareNameForCSV(value),
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'eventDate',
        headerName: 'Event Date',
        flex: 1,
        minWidth: 150,
        type: 'date',
        valueFormatter: value => (value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '-'),
        valueGetter: value => value && new Date(value),
        renderCell: ({ row }) => {
          const content = moment(row.eventDate).format(DD_SLASH_MMM_SLASH_YYYY);
          return (
            <div className="overflow-ellipsis" title={content}>
              {content}
            </div>
          );
        },
        filterOperators: allDateOperators
      },
      {
        field: 'revenueDate',
        headerName: 'Revenue Date',
        flex: 1,
        minWidth: 150,
        type: 'date',
        valueFormatter: value => (value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '-'),
        valueGetter: value => value && new Date(value),
        renderCell: ({ row: { revenueDate } }) => (
          <div>{revenueDate ? moment(revenueDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
        ),
        filterOperators: allDateOperators
      },
      {
        field: 'invoiceDate',
        headerName: 'Invoice Date',
        flex: 1,
        minWidth: 150,
        type: 'date',
        valueFormatter: value => (value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '-'),
        valueGetter: value => value && new Date(value),
        renderCell: ({ row: { invoiceDate } }) => (
          <div>{invoiceDate ? moment(invoiceDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
        ),
        filterOperators: allDateOperators
      },
      {
        field: 'invoiceAmount',
        headerName: 'Invoice Amount',
        flex: 1,
        minWidth: 170,
        type: 'number',
        valueGetter: (value, row) => toBill(row.invoiceAmount),
        renderCell: ({ row }) =>
          row.adjustmentSequence !== 0 ? '—' : <Amount coinsAmount={row.invoiceAmount} showDollarSign />,
        filterOperators: allNumberOperators
      },
      {
        field: 'paymentStatus',
        headerName: 'Sponsor Payment Status',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => calculateSponsorStatus(row),
        valueFormatter: value => prepareNameForCSV(value),
        renderCell: ({ value }) => (
          <StatusCell value={value} statusWidth="80px">
            <PaymentStatusIcon status={value} />
          </StatusCell>
        ),
        filterOperators: allSingleSelectOperators
      },
      {
        field: 'siteAmount',
        headerName: 'Site Amount',
        flex: 1,
        minWidth: 150,
        type: 'number',
        valueGetter: (value, row) => toBill(row.siteAmountWithWithholding),
        renderCell: ({ row }) => <Amount coinsAmount={row.siteAmountWithWithholding} showDollarSign />,
        filterOperators: allNumberOperators
      },
      {
        field: 'withholdingAmount',
        headerName: 'Withholding Amount',
        flex: 1,
        minWidth: 200,
        type: 'number',
        valueGetter: (value, row) => (isWithholdingType(row.sitePaymentType) ? null : toBill(row.withholdingAmount)),
        renderCell: ({ row, value }) =>
          isWithholdingType(row.sitePaymentType) ? (
            <DefaultFinanceCell value={value} />
          ) : (
            <Amount coinsAmount={row.withholdingAmount} showDollarSign />
          ),
        filterOperators: allNumberOperators
      },
      {
        field: 'sitePaymentType',
        headerName: 'Adjustment',
        flex: 1,
        minWidth: 150,
        valueGetter: value => {
          switch (value) {
            case SITE_CREDIT:
              return 'Site Credit';
            case SITE_DEBIT:
              return 'Site Debit';
            case WITHHOLDING:
            case SITE_CREDIT_WH:
            case SITE_DEBIT_WH:
              return 'Withholding';
            case PARENT:
              return 'N/A';
            default:
              return '—';
          }
        },
        filterOperators: allStringOperators
      },
      {
        field: 'sitePaidAmount',
        headerName: 'Site Paid Amount',
        flex: 1,
        minWidth: 200,
        type: 'number',
        valueGetter: (value, row) => toBill(row.sitePaidAmount),
        renderCell: ({ row }) => <Amount coinsAmount={row.sitePaidAmount} showDollarSign />,
        filterOperators: allNumberOperators
      },
      {
        field: 'sitePaymentStatus',
        headerName: 'Site Payment Status',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => SitePaymentStatus[row.sitePaymentStatus],
        renderCell: ({ value }) => (
          <StatusCell value={value} statusWidth="110px">
            <SitePaymentStatusIcon status={value} />
          </StatusCell>
        ),
        filterOperators: allStringOperators
      },
      {
        field: 'sitePaymentDate',
        headerName: 'Site Payment Date',
        flex: 1,
        minWidth: 160,
        type: 'date',
        filterOperators: overrideBeforeDateOperator('sitePaymentDate'),
        valueFormatter: sitePaymentsDateValueFormatter,
        valueGetter: sitePaymentsValueGetter,
        renderCell: sitePaymentsDateRenderCell
      },
      {
        field: 'siteBillNumber',
        headerName: 'Site Bill #',
        flex: 1,
        minWidth: 150,
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'eventNumber',
        headerName: 'Event ID',
        flex: 1,
        minWidth: 125,
        sortComparator: eventNumberComparator,
        valueGetter: (value, row) => {
          if (row.adjustmentSequence === 0) {
            return resolveEventSuffix(row.eventNumber, row.sitePaymentType);
          }
          return resolveEventSuffix(`${row.eventNumber}.${row.adjustmentSequence}`, row.sitePaymentType);
        },
        filterOperators: allStringOperators
      },
      {
        field: 'investigators',
        headerName: 'Investigator',
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => (row?.investigators ? row.investigators : 'N/A'),
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'patientId',
        headerName: 'Patient ID',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'adjustmentMemo',
        headerName: 'Adjustment Memo',
        flex: 1,
        minWidth: 175,
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'currentPatientStatus',
        headerName: 'Current Patient Status',
        flex: 1,
        minWidth: 155,
        valueGetter: (value, row) => row.currentPatientStatus,
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'eventPatientStatus',
        headerName: 'Event Patient Status',
        flex: 1,
        minWidth: 155,
        valueGetter: (value, row) => convertPatientStatus(row.eventPatientStatus),
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'invoiceNumber',
        headerName: 'Invoice Number',
        flex: 1,
        minWidth: 120,
        sortComparator: eventNumberComparator,
        valueGetter: (value, { invoiceNumber, sitePaymentType }) =>
          invoiceNumber ? resolveEventSuffix(invoiceNumber, sitePaymentType) : '',
        renderCell: DefaultFinanceCell,
        filterOperators: allStringOperators
      },
      {
        field: 'visitDate',
        headerName: 'Visit Date',
        flex: 1,
        minWidth: 150,
        type: 'date',
        filterOperators: overrideBeforeDateOperator('visitDate'),
        valueFormatter: sitePaymentsDateValueFormatter,
        valueGetter: sitePaymentsValueGetter,
        renderCell: ({ row: { visitDate } }) => <DefaultDateRenderCell date={visitDate} />
      },
      {
        field: 'expenseEventComment',
        headerName: 'Expense Comments',
        flex: 1,
        minWidth: 190,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        filterOperators: allStringOperators
      }
    ];

    return {
      columns: userAbleToSeeARInfo ? defaultColumns : defaultColumns.filter(col => !ARColumns.includes(col.field)),
      tableVersion: '1.02',
      tableName: 'SITE_FINANCE'
    };
  }, [userAbleToSeeARInfo]);
};
